import { resolveMediaUrl } from '../../../utils/mediaSas';

/** Supported chat reaction types (matches mobile ConversationScreen SUPPORTED_REACTIONS). */
export const SUPPORTED_REACTIONS = [
  { type: 'love', emoji: '❤️' },
  { type: 'like', emoji: '👍' },
  { type: 'haha', emoji: '😂' },
  { type: 'wow', emoji: '😮' },
  { type: 'sad', emoji: '😢' },
  { type: 'fire', emoji: '🔥' },
];

export const SUPPORTED_REACTION_TYPES = SUPPORTED_REACTIONS.map((reaction) => reaction.type);

const REACTION_EMOJI_BY_TYPE = Object.fromEntries(
  SUPPORTED_REACTIONS.map((reaction) => [reaction.type, reaction.emoji]),
);

export const isSupportedReactionType = (type) =>
  Boolean(type && REACTION_EMOJI_BY_TYPE[String(type).toLowerCase()]);

export const getReactionEmoji = (type) =>
  REACTION_EMOJI_BY_TYPE[String(type ?? '').toLowerCase()] ?? null;

const collectUserIds = (user = {}, fallbackUserId = null) => {
  const ordered = [
    user.partner_profile_id,
    user.partnerProfileId,
    user.db_user_id,
    user.id,
    user.userId,
    user.uid,
    user.user_id,
    user.firebase_uid,
    user.firebaseUid,
    fallbackUserId,
  ];
  const ids = [];
  const seen = new Set();

  ordered.forEach((value) => {
    const normalized = String(value ?? '').trim();
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      ids.push(normalized);
    }
  });

  return ids;
};

const normalizeReactionAvatarUrl = (user = {}) => {
  const raw = user.avatarUrl ?? user.avatar ?? user.image ?? undefined;
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  return resolveMediaUrl(trimmed);
};

const getReactionUserMatchIds = (user = {}) =>
  [...(user?.matchIds ?? []), user?.id, user?.uid]
    .map((id) => String(id ?? '').trim())
    .filter(Boolean);

const normalizeReactionUser = (user = {}, { userId } = {}) => {
  if (!user || typeof user !== 'object') {
    const matchIds = collectUserIds({}, userId);
    return {
      id: matchIds[0] ?? null,
      matchIds,
      fullName: 'Member',
      avatarUrl: undefined,
    };
  }

  const fullName =
    user.fullName ?? user.name ?? user.displayName ?? user.username ?? user.user_name ?? 'Member';
  const matchIds = collectUserIds(user, userId);

  return {
    id: matchIds[0] ?? null,
    matchIds,
    fullName: String(fullName),
    avatarUrl: normalizeReactionAvatarUrl(user),
  };
};

/** Fill missing reaction name/avatar from a known identity (cache, member, partner profile). */
export const mergeReactionUserIdentity = (user, identity) => {
  if (!user || typeof user !== 'object' || !identity || typeof identity !== 'object') {
    return user;
  }

  const currentName = String(user.fullName ?? '').trim();
  const hasName = Boolean(currentName) && currentName.toLowerCase() !== 'member';
  const identityName = String(identity.fullName ?? identity.author ?? identity.name ?? '').trim();
  const hasAvatar = Boolean(String(user.avatarUrl ?? '').trim());
  const identityAvatar = normalizeReactionAvatarUrl(identity);

  return {
    ...user,
    fullName: hasName ? user.fullName : identityName || user.fullName,
    avatarUrl: hasAvatar ? user.avatarUrl : identityAvatar || user.avatarUrl,
  };
};

/**
 * Enrich latest_reactions users via a lookup (messages / members / sender defaults).
 * lookup(user) → { fullName?, avatarUrl? } | null
 */
export const enrichLatestReactionsWithIdentityLookup = (latestReactions, lookup) => {
  if (!Array.isArray(latestReactions) || typeof lookup !== 'function') {
    return latestReactions;
  }

  return latestReactions.map((entry) => {
    if (!entry?.user) {
      return entry;
    }
    const identity = lookup(entry.user);
    if (!identity) {
      return entry;
    }
    return {
      ...entry,
      user: mergeReactionUserIdentity(entry.user, identity),
    };
  });
};

/** Prefer prior reactor avatar/name when a socket replace omits them. */
export const mergeLatestReactionIdentitiesFromPrevious = (incoming, previous = []) => {
  if (!Array.isArray(incoming) || incoming.length === 0 || !Array.isArray(previous)) {
    return incoming;
  }
  if (previous.length === 0) {
    return incoming;
  }

  const priorByKey = new Map();
  previous.forEach((entry) => {
    const type = String(entry?.type ?? '').toLowerCase();
    if (!type || !entry?.user) {
      return;
    }
    getReactionUserMatchIds(entry.user).forEach((id) => {
      priorByKey.set(`${type}:${id}`, entry.user);
    });
  });

  return incoming.map((entry) => {
    const type = String(entry?.type ?? '').toLowerCase();
    if (!type || !entry?.user) {
      return entry;
    }
    const priorUser = getReactionUserMatchIds(entry.user)
      .map((id) => priorByKey.get(`${type}:${id}`))
      .find(Boolean);
    if (!priorUser) {
      return entry;
    }
    return {
      ...entry,
      user: mergeReactionUserIdentity(entry.user, priorUser),
    };
  });
};

/** Collect ids for the signed-in partner user to match reaction authors. */
export const getPartnerCommunityDisplayName = (partnerProfile) =>
  String(partnerProfile?.fullName ?? '').trim();

export const buildPartnerCommunityCurrentUserIds = ({ firebaseUid, partnerProfile } = {}) => {
  const ids = new Set();

  if (partnerProfile) {
    collectUserIds(partnerProfile).forEach((id) => ids.add(id));
  }

  if (firebaseUid) {
    ids.add(String(firebaseUid).trim());
  }

  return ids;
};

const isPartnerReactionAuthor = (reactionUser, partnerDisplayName) => {
  const normalizedName = String(partnerDisplayName ?? '')
    .trim()
    .toLowerCase();
  if (!normalizedName) {
    return false;
  }

  return (
    String(reactionUser?.fullName ?? '')
      .trim()
      .toLowerCase() === normalizedName
  );
};

export const isCurrentUserReaction = (reactionUser, currentUserIds) => {
  if (!reactionUser || !currentUserIds?.size) {
    return false;
  }

  const candidateIds = Array.isArray(reactionUser.matchIds)
    ? reactionUser.matchIds
    : collectUserIds(reactionUser, reactionUser.id);

  return candidateIds.some((id) => currentUserIds.has(String(id)));
};

/** True when this chat message was sent by the signed-in partner/admin user. */
export const isCurrentUserChatMessage = (message, currentUserIds) => {
  if (!message || typeof message !== 'object' || !currentUserIds?.size) {
    return false;
  }

  const sender = message.sender && typeof message.sender === 'object' ? message.sender : {};
  const user = message.user && typeof message.user === 'object' ? message.user : {};
  const candidateIds = [
    ...collectUserIds(sender, message.senderId ?? message.senderUid ?? null),
    ...collectUserIds(user),
    message.senderUid,
    message.senderId,
  ];

  return candidateIds.some((id) => {
    const normalized = String(id ?? '').trim();
    return Boolean(normalized) && currentUserIds.has(normalized);
  });
};

/** Infer the signed-in user's reaction types from latest_reactions when own_reactions is empty. */
export const deriveOwnReactionsFromLatest = (
  latestReactions = [],
  { currentUserIds, partnerDisplayName } = {},
) => {
  const types = [];
  const seen = new Set();

  latestReactions.forEach((entry) => {
    const type = entry?.type;
    if (!type || seen.has(type)) {
      return;
    }

    const matchesCurrentUser =
      isCurrentUserReaction(entry.user, currentUserIds) ||
      isPartnerReactionAuthor(entry.user, partnerDisplayName);

    if (!matchesCurrentUser) {
      return;
    }

    seen.add(type);
    types.push(type);
  });

  return types;
};

/** Resolve all reaction types the current user has on a message. */
export const getMessageOwnReactionTypes = (
  message,
  { currentUserIds, partnerDisplayName } = {},
) => {
  if (message?.ownReactions?.length) {
    return message.ownReactions;
  }

  if (message?.ownReaction) {
    return [message.ownReaction];
  }

  const derived = deriveOwnReactionsFromLatest(message?.latestReactions ?? [], {
    currentUserIds,
    partnerDisplayName,
  });
  if (derived.length) {
    return derived;
  }

  return (message?.reactionChips ?? []).filter((chip) => chip.isOwn).map((chip) => chip.type);
};

/** Fallback counts from Stream `reaction_groups` when `reaction_counts` is absent. */
export const buildReactionCountsFromGroups = (reactionGroups) => {
  if (!reactionGroups || typeof reactionGroups !== 'object' || Array.isArray(reactionGroups)) {
    return {};
  }

  return Object.entries(reactionGroups).reduce((acc, [type, group]) => {
    const normalizedType = String(type).toLowerCase();
    const count = Number(group?.count);
    if (!isSupportedReactionType(normalizedType) || !Number.isFinite(count) || count <= 0) {
      return acc;
    }
    acc[normalizedType] = count;
    return acc;
  }, {});
};

const buildLatestReactionsFromGroups = (reactionGroups) => {
  if (!reactionGroups || typeof reactionGroups !== 'object' || Array.isArray(reactionGroups)) {
    return [];
  }

  return Object.entries(reactionGroups).flatMap(([type, group]) => {
    const normalizedType = String(type).toLowerCase();
    if (!isSupportedReactionType(normalizedType)) return [];

    const entries = Array.isArray(group?.latest_reactions_by) ? group.latest_reactions_by : [];
    return entries.map((entry) => ({
      type: normalizedType,
      created_at: entry?.created_at ?? entry?.createdAt ?? group?.last_reaction_at ?? null,
      user_id: entry?.user_id ?? entry?.userId,
      user: entry?.user ?? entry?.author,
    }));
  });
};

/** Normalize Stream `latest_reactions` entries for UI + detail sheet. */
export const normalizeLatestReactions = (latestReactions) => {
  if (!Array.isArray(latestReactions)) return [];

  return latestReactions
    .map((entry) => {
      const type = String(entry?.type ?? entry?.reaction_type ?? '').toLowerCase();
      if (!isSupportedReactionType(type)) return null;

      const userId = entry?.user_id ?? entry?.userId ?? null;

      return {
        type,
        emoji: getReactionEmoji(type),
        createdAt: entry?.created_at ?? entry?.createdAt ?? entry?.updated_at ?? null,
        user: normalizeReactionUser(entry?.user ?? entry?.author, { userId }),
      };
    })
    .filter(Boolean);
};

/** Extract all current-user reaction types from `own_reactions`. */
export const normalizeOwnReactions = (ownReactions) => {
  if (!ownReactions) return [];

  const entries = Array.isArray(ownReactions)
    ? ownReactions
    : typeof ownReactions === 'object'
      ? [ownReactions]
      : [];

  const types = [];
  const seen = new Set();

  for (const entry of entries) {
    const type = String(entry?.type ?? entry?.reaction_type ?? entry ?? '').toLowerCase();
    if (isSupportedReactionType(type) && !seen.has(type)) {
      seen.add(type);
      types.push(type);
    }
  }

  return types;
};

/** Extract current user's reaction type from `own_reactions` (enforce_unique → one). */
export const normalizeOwnReaction = (ownReactions) =>
  normalizeOwnReactions(ownReactions)[0] ?? null;

/** Normalize `{ like: 3, love: 1 }` counts, dropping zero/invalid keys. */
export const normalizeReactionCounts = (reactionCounts) => {
  if (!reactionCounts || typeof reactionCounts !== 'object' || Array.isArray(reactionCounts)) {
    return {};
  }

  return Object.entries(reactionCounts).reduce((acc, [type, count]) => {
    const normalizedType = String(type).toLowerCase();
    const normalizedCount = Number(count);
    if (
      !isSupportedReactionType(normalizedType) ||
      !Number.isFinite(normalizedCount) ||
      normalizedCount <= 0
    ) {
      return acc;
    }
    acc[normalizedType] = normalizedCount;
    return acc;
  }, {});
};

/**
 * Build ordered reaction chips for display under a message.
 * Order follows latest_reactions, then remaining types by count desc.
 */
export const buildReactionChips = ({
  reactionCounts = {},
  ownReaction = null,
  ownReactions = [],
  latestReactions = [],
} = {}) => {
  const counts = normalizeReactionCounts(reactionCounts);
  const typesWithCounts = Object.keys(counts);
  if (typesWithCounts.length === 0) return [];

  const ownTypes = new Set(
    ownReactions.length > 0 ? ownReactions : ownReaction ? [ownReaction] : [],
  );
  const orderedTypes = [];
  const seen = new Set();

  latestReactions.forEach((entry) => {
    const type = entry?.type;
    if (!type || seen.has(type) || !counts[type]) return;
    seen.add(type);
    orderedTypes.push(type);
  });

  typesWithCounts
    .filter((type) => !seen.has(type))
    .sort((a, b) => counts[b] - counts[a] || a.localeCompare(b))
    .forEach((type) => {
      orderedTypes.push(type);
    });

  return orderedTypes.map((type) => ({
    type,
    emoji: getReactionEmoji(type),
    count: counts[type],
    isOwn: ownTypes.has(type),
  }));
};

/** Group latest reactions by type for the detail sheet. */
export const groupLatestReactionsByType = (latestReactions = []) => {
  const groups = new Map();

  latestReactions.forEach((entry) => {
    if (!entry?.type) return;
    const existing = groups.get(entry.type) ?? [];
    existing.push(entry);
    groups.set(entry.type, existing);
  });

  return SUPPORTED_REACTION_TYPES.filter((type) => groups.has(type)).map((type) => ({
    type,
    emoji: getReactionEmoji(type),
    reactions: groups.get(type) ?? [],
  }));
};

/**
 * Detail-sheet groups. Prefer latest_reactions; if omitted but counts exist (optimistic /
 * thin API), synthesize rows so the sheet is never empty while chips are visible.
 */
export const buildReactionDetailGroups = (message) => {
  const fromLatest = groupLatestReactionsByType(
    normalizeLatestReactions(message?.latestReactions ?? []),
  );
  if (fromLatest.length > 0) {
    return fromLatest;
  }

  const counts = normalizeReactionCounts(message?.reactionCounts ?? {});
  const ownTypes = new Set(
    message?.ownReactions?.length
      ? message.ownReactions
      : message?.ownReaction
        ? [message.ownReaction]
        : [],
  );

  return SUPPORTED_REACTION_TYPES.filter((type) => (counts[type] ?? 0) > 0).map((type) => {
    const count = counts[type];
    const reactions = [];

    if (ownTypes.has(type)) {
      reactions.push({
        type,
        emoji: getReactionEmoji(type),
        user: {
          id: 'self',
          matchIds: ['self'],
          fullName: 'You',
          avatarUrl: undefined,
        },
      });
    }

    const remaining = Math.max(0, count - reactions.length);
    for (let index = 0; index < remaining; index += 1) {
      reactions.push({
        type,
        emoji: getReactionEmoji(type),
        user: {
          id: `member-${type}-${index}`,
          matchIds: [],
          fullName: 'Member',
          avatarUrl: undefined,
        },
      });
    }

    return {
      type,
      emoji: getReactionEmoji(type),
      reactions,
    };
  });
};

const reactionEntryMatchesUser = (entry, user) => {
  if (!entry?.user || !user) return false;
  const entryIds = new Set(
    [...(entry.user.matchIds ?? []), entry.user.id, entry.user.uid]
      .map((id) => String(id ?? '').trim())
      .filter(Boolean),
  );
  const userIds = new Set(collectUserIds(user));
  if ([...userIds].some((id) => entryIds.has(id))) {
    return true;
  }

  const entryName = String(entry.user.fullName ?? '')
    .trim()
    .toLowerCase();
  const userName = String(user.fullName ?? user.name ?? '')
    .trim()
    .toLowerCase();
  return Boolean(entryName && userName && entryName === userName);
};

const buildOptimisticLatestEntry = (type, user) => ({
  type,
  emoji: getReactionEmoji(type),
  createdAt: new Date().toISOString(),
  user: normalizeReactionUser(user ?? { fullName: 'You' }),
});

export const mapMessageReactionFields = (
  message = {},
  { currentUserIds, partnerDisplayName } = {},
) => {
  const reactionGroups = message.reaction_groups ?? message.reactionGroups ?? null;
  const countsSource =
    message.reaction_counts ??
    message.reactionCounts ??
    buildReactionCountsFromGroups(reactionGroups);
  const reactionCounts = normalizeReactionCounts(countsSource);

  const latestSource =
    message.latest_reactions ??
    message.latestReactions ??
    buildLatestReactionsFromGroups(reactionGroups);
  const latestReactions = normalizeLatestReactions(latestSource);

  let ownReactions = normalizeOwnReactions(message.own_reactions ?? message.ownReactions);
  if (ownReactions.length === 0) {
    ownReactions = deriveOwnReactionsFromLatest(latestReactions, {
      currentUserIds,
      partnerDisplayName,
    });
  }
  const ownReaction = ownReactions[0] ?? null;

  return {
    reactionCounts,
    ownReaction,
    ownReactions,
    latestReactions,
    reactionChips: buildReactionChips({
      reactionCounts,
      ownReaction,
      ownReactions,
      latestReactions,
    }),
  };
};

/** Apply a local add for optimistic UI. */
export const applyOptimisticAddReaction = (message, type, { user } = {}) => {
  if (!isSupportedReactionType(type)) {
    return message;
  }

  const reactionCounts = { ...(message?.reactionCounts ?? {}) };
  reactionCounts[type] = (reactionCounts[type] ?? 0) + 1;

  const ownReactions = [...new Set([...(message?.ownReactions ?? []), type])];
  const ownReaction = ownReactions[ownReactions.length - 1] ?? type;

  let latestReactions = Array.isArray(message?.latestReactions) ? [...message.latestReactions] : [];

  const alreadyPresent = latestReactions.some(
    (entry) => entry?.type === type && reactionEntryMatchesUser(entry, user),
  );

  if (!alreadyPresent) {
    latestReactions = [buildOptimisticLatestEntry(type, user), ...latestReactions];
  }

  return {
    ...message,
    reactionCounts,
    ownReaction,
    ownReactions,
    latestReactions,
    reactionChips: buildReactionChips({
      reactionCounts,
      ownReaction,
      ownReactions,
      latestReactions,
    }),
  };
};

/** Apply a local remove for optimistic UI. */
export const applyOptimisticRemoveReaction = (message, type, { user } = {}) => {
  if (!isSupportedReactionType(type)) {
    return message;
  }

  const reactionCounts = { ...(message?.reactionCounts ?? {}) };

  reactionCounts[type] = Math.max(0, (reactionCounts[type] ?? 0) - 1);
  if (reactionCounts[type] <= 0) {
    delete reactionCounts[type];
  }

  const ownReactions = (message?.ownReactions ?? []).filter((ownType) => ownType !== type);
  const ownReaction = ownReactions[0] ?? null;

  let latestReactions = Array.isArray(message?.latestReactions) ? [...message.latestReactions] : [];

  if (user) {
    latestReactions = latestReactions.filter(
      (entry) => !(entry?.type === type && reactionEntryMatchesUser(entry, user)),
    );
  } else if (!reactionCounts[type]) {
    latestReactions = latestReactions.filter((entry) => entry?.type !== type);
  }

  return {
    ...message,
    reactionCounts,
    ownReaction,
    ownReactions,
    latestReactions,
    reactionChips: buildReactionChips({
      reactionCounts,
      ownReaction,
      ownReactions,
      latestReactions,
    }),
  };
};

/**
 * Apply a remote reaction add from socket (does not mark as own unless isOwn).
 * Used when backend sends minimal { reactionType, userId } without full Stream state.
 */
export const applyRemoteReactionAdded = (message, type, { isOwn = false, user } = {}) => {
  if (!isSupportedReactionType(type)) {
    return message;
  }

  const reactionCounts = { ...(message?.reactionCounts ?? {}) };
  reactionCounts[type] = (reactionCounts[type] ?? 0) + 1;

  let ownReactions = [...(message?.ownReactions ?? [])];
  let ownReaction = message?.ownReaction ?? null;
  if (isOwn) {
    ownReactions = [...new Set([...ownReactions, type])];
    ownReaction = type;
  }

  let latestReactions = Array.isArray(message?.latestReactions) ? [...message.latestReactions] : [];

  const reactor = user ?? (isOwn ? { fullName: 'You' } : { fullName: 'Member' });
  const alreadyPresent = latestReactions.some(
    (entry) => entry?.type === type && reactionEntryMatchesUser(entry, reactor),
  );
  if (!alreadyPresent) {
    latestReactions = [buildOptimisticLatestEntry(type, reactor), ...latestReactions];
  }

  return {
    ...message,
    reactionCounts,
    ownReaction,
    ownReactions,
    latestReactions,
    reactionChips: buildReactionChips({
      reactionCounts,
      ownReaction,
      ownReactions,
      latestReactions,
    }),
  };
};

/** Apply a remote reaction remove from socket. */
export const applyRemoteReactionRemoved = (message, type, { isOwn = false, user } = {}) => {
  if (!isSupportedReactionType(type)) {
    return message;
  }

  const reactionCounts = { ...(message?.reactionCounts ?? {}) };
  reactionCounts[type] = Math.max(0, (reactionCounts[type] ?? 0) - 1);
  if (reactionCounts[type] <= 0) {
    delete reactionCounts[type];
  }

  let ownReactions = [...(message?.ownReactions ?? [])];
  let ownReaction = message?.ownReaction ?? null;
  if (isOwn) {
    ownReactions = ownReactions.filter((ownType) => ownType !== type);
    ownReaction = ownReactions[0] ?? null;
  }

  let latestReactions = Array.isArray(message?.latestReactions) ? [...message.latestReactions] : [];

  if (user) {
    latestReactions = latestReactions.filter(
      (entry) => !(entry?.type === type && reactionEntryMatchesUser(entry, user)),
    );
  } else if (!reactionCounts[type]) {
    latestReactions = latestReactions.filter((entry) => entry?.type !== type);
  } else if (isOwn) {
    // Drop one matching "You"/own-looking entry for this type when counts remain.
    let removed = false;
    latestReactions = latestReactions.filter((entry) => {
      if (removed || entry?.type !== type) return true;
      if (String(entry?.user?.fullName ?? '').toLowerCase() === 'you') {
        removed = true;
        return false;
      }
      return true;
    });
  }

  return {
    ...message,
    reactionCounts,
    ownReaction,
    ownReactions,
    latestReactions,
    reactionChips: buildReactionChips({
      reactionCounts,
      ownReaction,
      ownReactions,
      latestReactions,
    }),
  };
};

/** Apply a local toggle/switch for optimistic UI (enforce_unique). */
export const applyOptimisticReaction = (message, nextType) => {
  const reactionCounts = { ...(message?.reactionCounts ?? {}) };
  const currentOwn = message?.ownReaction ?? null;
  let ownReaction = currentOwn;

  if (currentOwn === nextType) {
    reactionCounts[nextType] = Math.max(0, (reactionCounts[nextType] ?? 0) - 1);
    if (reactionCounts[nextType] <= 0) delete reactionCounts[nextType];
    ownReaction = null;
  } else {
    if (currentOwn) {
      reactionCounts[currentOwn] = Math.max(0, (reactionCounts[currentOwn] ?? 0) - 1);
      if (reactionCounts[currentOwn] <= 0) delete reactionCounts[currentOwn];
    }
    reactionCounts[nextType] = (reactionCounts[nextType] ?? 0) + 1;
    ownReaction = nextType;
  }

  const latestReactions = Array.isArray(message?.latestReactions)
    ? [...message.latestReactions]
    : [];

  return {
    ...message,
    reactionCounts,
    ownReaction,
    latestReactions,
    reactionChips: buildReactionChips({ reactionCounts, ownReaction, latestReactions }),
  };
};
